import React, { useState } from 'react';
import { Button } from '../components/shared/Button';
import { LogoWithKonami } from '../components/layout/LogoWithKonami';
import { pseudoToColor } from '../utils/generateId';
import type { User } from '../types';
import type { Direction } from '../hooks/useKonamiCode';

interface PseudoSetupProps {
  onComplete: (user: User) => void;
  onDirectionClick?: (dir: Direction) => void;
  progressLength?: number;
}

export function PseudoSetup({ onComplete, onDirectionClick, progressLength }: PseudoSetupProps) {
  const [pseudo, setPseudo] = useState('');

  const handleSubmit = () => {
    const trimmed = pseudo.trim();
    if (trimmed.length < 2) return;
    onComplete({
      pseudo: trimmed,
      color: pseudoToColor(trimmed),
    });
  };

  const previewColor = pseudo.trim().length >= 2 ? pseudoToColor(pseudo.trim()) : '#7c8794';

  return (
    <div className="min-h-screen bg-namm-bg flex items-center justify-center p-5">
      <div className="w-full max-w-sm border border-namm-line/40 bg-namm-panel/70 p-8 text-center">
        {/* Clickable 4-quadrant Logo */}
        <div className="flex justify-center mb-6">
          <LogoWithKonami onDirectionClick={onDirectionClick} progressLength={progressLength} />
        </div>

        <p className="text-xs font-mono text-namm-muted mb-8">
          Choisissez un pseudo pour commencer
        </p>

        {/* Avatar preview */}
        <div
          className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-xl font-bold text-namm-bg transition-colors duration-300"
          style={{ backgroundColor: previewColor }}
        >
          {pseudo.trim() ? pseudo.trim().charAt(0).toUpperCase() : '?'}
        </div>

        {/* Input */}
        <input
          type="text"
          value={pseudo}
          onChange={(e) => setPseudo(e.target.value)}
          placeholder="Votre pseudo..."
          className="w-full bg-namm-panel-soft border border-namm-line px-4 py-3 text-sm font-mono text-namm-text text-center outline-none focus:border-namm-cyan placeholder:text-namm-muted/50 mb-5"
          maxLength={20}
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
        />

        <Button
          variant="primary"
          className="w-full"
          onClick={handleSubmit}
          disabled={pseudo.trim().length < 2}
        >
          C'est parti
        </Button>

        <p className="text-[10px] font-mono text-namm-muted/50 mt-4">
          Modifiable à tout moment
        </p>
      </div>
    </div>
  );
}
